import { useEffect, useRef, useState } from "react";
import { Dropdown, Menu, message, TableColumnsType, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { ReactComponent as MenuIcon } from "@assets/images/vertical-dots.svg";

import Table from "@components/Table";
import Button from "@components/Button";
import { checkPermission } from "@utils";
import { permissions } from "@router";
import {
  ErrorServices,
  useDeleteCompanyJobFunctionMutation,
  useFetchPaginatedCompanyJobFunctionsQuery,
  useDownloadCompanyJobFunctionsMutation,
  useUploadCompanyJobFunctionsMutation,
  useFetchCompaniesQuery,
} from "@services";
import AddCompanyJobFunction from "./AddJobFunction";
import EditCompanyJobFunction from "./EditJobFunction";
import { useTypedSelector } from "@/hooks";
// import { handleCsvUpload } from "@/utils/uploadCsv";
import CompanyList from "@/components/List/company-list";
import Upload from "@/components/Upload";
import { GoPencil } from "react-icons/go";
import { BsTrash3 } from "react-icons/bs";

type JobSubFunctionType = {
  id: number;
  name: string;
  description?: string | null;
};

type JobFunctionType = {
  id: number;
  name: string;
  description?: string | null;
  sub_job_functions: Array<JobSubFunctionType>;
};

const CompanyJobFunction = () => {
  const dispatch = useDispatch();
  const { user, permissions: per } = useTypedSelector(state => state.auth);
  const { company_id } = useParams<{ company_id: string }>();
  const companyID = company_id || user?.company?.id;
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const jf_id = useRef<any>(null);
  // const inputRef = useRef<any>(null);

  const [isAddJFVisible, setIsAddJFVisible] = useState(false);
  const [isEditJFVisible, setIsEditJFVisible] = useState(false);
  const [jfItem, setJfItem] = useState<JobFunctionType | {}>({});

  const [companyId, setCompanyId] = useState<number | null>(Number(companyID));
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies = companiesData?.data || [];

  const { data: jobFunctionsData, isLoading: isLoadingJobFunctions } =
    useFetchPaginatedCompanyJobFunctionsQuery(
      {
        company_id: companyId!,
        page,
        page_size: pageSize,
      },
      {
        skip: !companyId, // ✅ added
      }
    );

  const jobFunctions = jobFunctionsData?.data || [];
  const pagination = jobFunctionsData?.pagination;

  const [deleteJobFunction] = useDeleteCompanyJobFunctionMutation();
  const [uploadJobFunction, { isLoading: isUploading }] =
    useUploadCompanyJobFunctionsMutation();

  const [downloadJobFunctions, { isLoading }] =
    useDownloadCompanyJobFunctionsMutation();

  const handleActionDropdown = ({
    item,
    key,
    domEvent,
  }: {
    key: string;
    domEvent:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>;
    item: JobFunctionType;
  }) => {
    domEvent.stopPropagation();

    if (key === "2") {
      removeJobFunction(item?.id.toString());
    } else {
      onEditJf(item);
    }
  };

  const columns: TableColumnsType<JobFunctionType> = [
    {
      title: "Company Job Functions",
      key: "name",
      dataIndex: "name",
      width: "20%",
    },
    {
      title: "Company Job Sub-Functions",
      key: "sub_job_functions",
      width: "55%",
      render: ({ sub_job_functions }) => {
        if (!Array.isArray(sub_job_functions) || !sub_job_functions.length) {
          return null;
        }

        const names = sub_job_functions.map(
          (val: JobSubFunctionType) => val.name
        );

        const visibleCount = 4;
        const remaining = names.length - visibleCount;

        return (
          <Tooltip title={names.join(", ")}>
            <span>
              {names.slice(0, visibleCount).join(", ")}
              {remaining > 0 && (
                <span className="text-blue-500 text-center text-xs font-semibold mx-2">
                  {" ... "}+{remaining}
                </span>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    ...((checkPermission(permissions.UPDATE_COMPANY_JOB_FUNCTION) ||
    checkPermission(permissions.DELETE_COMPANY_JOB_FUNCTION)
      ? [
          {
            title: "Actions",
            key: "action",
            width: 160,
            align: "center",
            render: (item: JobFunctionType) => (
              <div className="flex items-center gap-2">
                {checkPermission(permissions.UPDATE_COMPANY_JOB_FUNCTION) && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleActionDropdown({
                        item,
                        key: "1",
                        domEvent: e,
                      });
                    }}
                    className="table__action__btn table__action__btn--client"
                  >
                    <GoPencil className="text-white mr-1 text-sm" /> Edit
                  </div>
                )}

                {checkPermission(permissions.DELETE_COMPANY_JOB_FUNCTION) && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleActionDropdown({
                        item,
                        key: "2",
                        domEvent: e,
                      });
                    }}
                    className="table__action__btn table__action__btn--delete"
                  >
                    <BsTrash3 className="text-white textx mr-1" /> Delete
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []) as any),
  ];

  const onRowClick = (data: JobFunctionType) => {
    history.push(
      `/company-job-functions/company/${companyId}/job_function/${data.id}`,
      {
        companyId: companyId,
      }
    );
  };

  const onEditJf = async (item: JobFunctionType) => {
    try {
      setJfItem(item);
      setIsEditJFVisible(true);
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const removeJobFunction = async (id: string) => {
    try {
      jf_id.current = id;

      await deleteJobFunction({
        company_id: companyId,
        company_job_function_id: id,
      }).unwrap();

      message.success("Company Job Function Deleted Successfully");
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  // const handleUploadClick = () => {
  //   if (isUploading) return;

  //   if (inputRef.current) {
  //     inputRef.current.value = ""; // reset (important)
  //     inputRef.current.click();
  //   }
  // };

  const handleUpload = async () => {
    if (!companyId || !uploadFile) return;

    try {
      const formData = new FormData();
      formData.append("attachment", uploadFile, uploadFile.name);

      const res = await uploadJobFunction({
        company_id: companyId,
        formData,
      }).unwrap();

      message.success(res?.message || "Uploaded successfully");
    } catch (error: any) {
      ErrorServices(error);
    }
  };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!companyId || isUploading) return;

  //   const file = e.target.files?.[0];

  //   // ❌ No file
  //   if (!file) {
  //     message.error("No file selected");
  //     return;
  //   }

  //   // ❌ Empty file (0 bytes)
  //   if (file.size === 0) {
  //     message.error("File is empty. Please upload a valid CSV.");
  //     return;
  //   }

  //   const reader = new FileReader();

  //   reader.onload = event => {
  //     const text = event.target?.result as string;

  //     const rows = text
  //       .split(/\r?\n/)
  //       .map(row => row.trim())
  //       .filter(row => row.length > 0);

  //     // ❌ Only headers or no real data
  //     if (rows.length <= 1) {
  //       message.error("CSV has no data rows");
  //       return;
  //     }

  //     // ✅ Proceed upload
  //     handleCsvUpload(
  //       e,
  //       formData =>
  //         uploadJobFunction({
  //           company_id: companyId,
  //           formData,
  //         }),
  //       {
  //         loadingMessage: "Uploading job functions...",
  //         successMessage: "Job Functions uploaded successfully",
  //       }
  //     );
  //   };

  //   reader.readAsText(file);
  // };

  const downloadFile = () => {
    if (!companyId || isLoading) return;

    message.loading({
      content: "Downloading Job Functions...",
      key: "job-download",
      duration: 0,
    });

    downloadJobFunctions({ company_id: companyId })
      .unwrap()
      .then(() => {
        message.success({
          content: "Download Started",
          key: "job-download",
        });
      })
      .catch((err: any) => {
        message.destroy("job-download");
        ErrorServices(err);
      });
  };

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        {isFetchingCompanies ? (
          <div
            style={{ height: "650px" }}
            className="flex justify-center items-center"
          >
            <LoadingOutlined className="spinner" style={{ fontSize: "80px" }} />
          </div>
        ) : (
          <main className="relative h-full bg-[#FAFAFA] px-8">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-10">
                <h1 className="text-4xl font-semibold text-[#4A4A4A] mb-3">
                  Select The Company
                </h1>

                <p className="text-sm font-medium text-[#6F6F6F]">
                  Choose A Company To Access And Manage Its Operations
                </p>
              </div>

              {/* Company Count */}
              <div className="flex justify-end mb-3">
                <span className="text-xs text-[#6F6F6F]">
                  {companies?.length || 0} of {companies?.length || 0} companies
                </span>
              </div>

              {/* Company List */}
              <div className="max-w-6xl">
                <CompanyList
                  companiesList={companies}
                  onSelectCompany={setSelectedCompany}
                  selectedCompany={selectedCompany}
                />
              </div>

              {/* Same Original Button Behavior */}
              {selectedCompany && (
                <div className="mt-10 flex justify-center absolute mx-auto left-0 right-0">
                  <button
                    onClick={() => setCompanyId(selectedCompany)}
                    className="
                    w-80
                    h-12
                    rounded
                    text-white
                    font-medium
                    transition-all
                    duration-200
                    hover:opacity-90
                  "
                    style={{
                      backgroundColor: "#F7935A",
                    }}
                  >
                    {
                      companies.find((item: any) => item.id == selectedCompany)
                        ?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      <AddCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsAddJFVisible}
        isVisible={isAddJFVisible}
      />

      <EditCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsEditJFVisible}
        isVisible={isEditJFVisible}
        jfItem={jfItem}
      />

      {/* <div className="appTitle text-base font-[500] mx-5">
        Company Job Functions
      </div> */}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar sticky top-0 z-10 bg-themeCore px-14 py-6 rounded-lg flex justify-between items-center my-7">
          <div className="title text-white font-[500] text-2xl uppercase">
            Company Job Functions & Sub Functions
          </div>

          <div className="action-buttons flex items-center gap-2">
            {checkPermission(permissions.CREATE_COMPANY_JOB_FUNCTION) && (
              <>
                {isUploadModal && (
                  <Upload
                    title="Upload Job Functions"
                    onSubmit={handleUpload}
                    isVisible={isUploadModal}
                    setIsVisible={setIsUploadModal}
                    file={uploadFile}
                    setFile={setUploadFile}
                  />
                )}

                <Button
                  variant="buttonWhite"
                  onClick={() => setIsUploadModal(true)}
                  disabled={isUploading}
                >
                  Upload Job Functions
                </Button>

                <Button
                  variant="buttonWhite"
                  onClick={downloadFile}
                  disabled={isLoading}
                >
                  Download Job Functions
                </Button>

                <Button
                  variant="buttonWhite"
                  onClick={() => setIsAddJFVisible(true)}
                >
                  Add New Job
                </Button>
              </>
            )}

            {!checkPermission(permissions.CREATE_COMPANY_JOB_FUNCTION) && (
              <Button
                variant="buttonWhite"
                onClick={downloadFile}
                disabled={isLoading}
              >
                Download Job Functions
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg">
          <Table
            data={jobFunctions}
            columns={columns}
            onRowClick={onRowClick}
            isLoading={isLoadingJobFunctions}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            onPageSizeChange={setPageSize}
            page={page}
          />
        </div>
      </div>
    </>
  );
};

export default CompanyJobFunction;
